import React from "react";
import { Checkbox, Form as AntdForm, Typography } from "antd";
import { Action, AnyObject } from "@triare/auth-redux";
import {
  Callbacks,
  Rule,
  ValidateErrorEntity,
} from "@rc-component/form/lib/interface";
import { FormProps as AntdFormProps } from "@rc-component/form/lib/Form";
import { NavLink } from "react-router";
import clsx from "clsx";
import {
  SignInByPhoneAction,
  SignInByUsernameAction,
} from "@triare/auth-redux/dist/saga/auth/signIn";
import { useAuth } from "../../../../../store/auth";
import AlertSuccess, { AlertSuccessProps } from "./Alert/Success";
import AlertError, { AlertErrorProps } from "./Alert/Error";
import Submit from "../../../../Common/Form/Button/Submit";

import styles from "../../index.module.scss";
import { useTranslation } from "react-i18next";

import { BRAND_NAME } from "../../../../../resource/brand";

interface FormProps<P> extends AntdFormProps {
  disableAlertSuccess?: true;
  requiredFields?: "*" | string[];
  validationRules: Rule;
  styles?: { [key: string]: string };

  children: React.ReactNode;
  alertSuccess?: AlertSuccessProps;
  alertError?: AlertErrorProps;

  signIn: (payload: P) => Action | void;
  onFinishFailed?: Callbacks<P>["onFinishFailed"];
  validateSubmit: (fieldsValue: AnyObject) => boolean;
}

function Form<P = SignInByPhoneAction | SignInByUsernameAction>({
  disableAlertSuccess,
  validationRules,
  validateSubmit,
  requiredFields = "*",
  styles: additionalStyles = {},
  form,
  children,
  alertSuccess,
  alertError,
  signIn,
  initialValues = { remember: true },
  onFinishFailed = (errorInfo: ValidateErrorEntity<P>) => {
    console.log("Failed:", errorInfo);
  },
  ...props
}: FormProps<P>): React.ReactNode {
  const {
    signIn: { loading, response },
  } = useAuth();
  const { t } = useTranslation();

  return (
    <AntdForm
      className={clsx(styles.form, additionalStyles?.form)}
      name="signIn"
      autoComplete="off"
      layout="vertical"
      scrollToFirstError
      onFinish={signIn}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      {...props}
    >
      <Typography.Title level={3} className={styles.text}>
        {t("signIn.heading", { brand: BRAND_NAME })}
      </Typography.Title>

      <div className={styles.alertWrapper}>
        {disableAlertSuccess ? null : (
          <AlertSuccess response={response} {...alertSuccess} />
        )}
        <AlertError {...alertError} />
      </div>

      {children}

      <AntdForm.Item>
        <AntdForm.Item
          className={clsx(styles?.remember, additionalStyles?.remember)}
          name="remember"
          valuePropName="checked"
          noStyle
        >
          <Checkbox>{t("common.rememberMe")}</Checkbox>
        </AntdForm.Item>

        <NavLink to="/forgot-password" style={{ float: "right" }}>
          {t("forgotPassword")}
        </NavLink>
      </AntdForm.Item>

      <Submit
        validateSubmit={validateSubmit}
        requiredFields={requiredFields}
        itemClassName={clsx(styles.submit, additionalStyles?.submit)}
        loading={loading}
        size="large"
      >
        {t("signIn.text")}
      </Submit>
    </AntdForm>
  );
}

export default Form;
